import { currencyExponent, formatMoney, minorToDecimal } from "@salon-pos/shared";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from "react";
import { ApiError } from "./api";
import { countryName } from "./countries";
import { currentLocale, hasKey, tr, useI18n, type MessageKey } from "./i18n";

/** Money in the branch/order currency, formatted with the branch locale (Intl). */
export function money(minor: number, currency: string, locale?: string) {
  try { return formatMoney(minor, currency, { locale: locale ?? "en-MY" }); } catch { return formatMoney(minor, currency); }
}

/** Fresh idempotency key per payment attempt; reuse the same key when retrying the SAME attempt. */
export function newKey(prefix = "pay"): string {
  const rnd = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now().toString(36);
  return prefix + "-" + rnd;
}

const CODE_KEYS: Record<string, MessageKey> = {
  network: "error.network", offline_payment: "mob.offline.payment", forbidden: "error.forbidden", onboarding_required: "error.onboarding", rate_limited: "error.rate", validation: "error.validation",
};

/** (Task iOS) plan / add-on / billing errors: neutral text in the store app (no plan names, prices or "upgrade") */
export const NATIVE_BILLING_CODES = new Set(["feature_not_enabled", "feature_disabled", "billing_read_only", "billing_suspended", "storage_paid_only", "branches_paid_only", "whatsapp_addon_inactive", "plan_limit", "storage_full", "storage_quota", "addon_required", "subscribe_first"]);

/** Flat string/number details of an API error, usable as {placeholders} in the translated message. */
const detailVars = (d: unknown): Record<string, string | number> => {
  const out: Record<string, string | number> = {};
  if (d && typeof d === "object" && !Array.isArray(d)) for (const [k, v] of Object.entries(d)) if (typeof v === "string" || typeof v === "number") out[k] = v;
  return out;
};

/**
 * User-facing, translated error text. Every API error code the POS / payment / checkout screens can receive has an
 * "apiError.<code>" key (en/zh/ms, see i18n/m/errors.ts and test/errors.test.ts), so raw server English never shows.
 * An unknown code falls back to the server message in English, and to a translated status message otherwise.
 */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    // (Security 1) account lockout: "Too many attempts. Try again in N minutes."
    if (e.code === "login_locked") return tr("apiError.login_locked", { minutes: 15, ...detailVars(e.details) });
    if (e.status === 429) return tr("error.rate");
    // (Task G) counter sale of units reserved for online orders
    if (e.code === "approval_required" && (e.details as { reason?: string } | undefined)?.reason === "reserved_stock") return tr("apiError.reserved_stock", detailVars(e.details));
    // (Task iOS, App Store 3.1.1 / 3.1.3) the store app never repeats the server's upgrade / add-on / billing wording
    if (import.meta.env.VITE_CLIENT === "native" && NATIVE_BILLING_CODES.has(e.code)) return tr("native.billingBlocked");
    // (phone input) "这个号码不像 新加坡 +65 的号码，是不是 马来西亚 +60 16-436 6293？"
    if (e.code === "invalid_phone") {
      const d = detailVars(e.details);
      if (d.suggestion && d.country && d.suggestionCountry) return tr("apiError.invalid_phone_hint", { ...d, country: countryName(String(d.country), currentLocale()), other: countryName(String(d.suggestionCountry), currentLocale()) });
      if (d.callingCode) return tr("apiError.invalid_phone", d);
    }
    if (CODE_KEYS[e.code]) return tr(CODE_KEYS[e.code]);
    for (const ns of ["signup.error.", "setup.error.", "verify.error.", "apiError."]) if (hasKey(ns + e.code)) return tr((ns + e.code) as MessageKey, detailVars(e.details));
    if (e.status >= 500 || e.status === 0) return tr("error.generic");
    if (currentLocale() === "en" && e.message && e.message !== "error") return e.message;
    const st = "error.status." + e.status;
    return hasKey(st) ? tr(st) : tr("error.generic");
  }
  return tr("error.generic");
}

/**
 * Last line of defence against silent no-op buttons: a request that fails inside a handler nobody awaited
 * (`onClick={() => void save()}`) becomes an unhandled rejection; show it as a translated error toast.
 */
export function installUnhandledErrorToasts(push: (text: string, kind: "ok" | "error") => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onRejection = (ev: PromiseRejectionEvent) => {
    if (!(ev.reason instanceof ApiError)) return; // programming errors stay in the console
    ev.preventDefault();
    push(errorText(ev.reason), "error");
  };
  window.addEventListener("unhandledrejection", onRejection);
  return () => window.removeEventListener("unhandledrejection", onRejection);
}

/** Load data with loading/error state and a reload function. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | null; error: string | null; loading: boolean; reload: () => Promise<void>; setData: (d: T) => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reload = useCallback(async () => {
    const n = ++seq.current;
    setLoading(true);
    try { const d = await fn(); if (n === seq.current) { setData(d); setError(null); } }
    catch (e) { if (n === seq.current) setError(errorText(e)); }
    finally { if (n === seq.current) setLoading(false); }
  }, deps);
  useEffect(() => { void reload(); }, [reload]);
  return { data, error, loading, reload, setData };
}

const PILL: Record<string, string> = {
  pending_payment: "warn", confirmed: "info", checked_in: "primary", in_service: "primary", completed: "ok", cancelled: "muted", no_show: "error",
  open: "info", held: "warn", paid: "ok", voided: "muted", closed: "muted", active: "ok", trialing: "info", onboarding: "warn", suspended: "error",
  // customer messages + WhatsApp templates
  sent: "info", delivered: "primary", read: "ok", failed: "error", skipped: "muted", queued: "warn", scheduled: "info", sending: "warn", canceled: "muted",
  draft: "muted", pending: "warn", approved: "ok", rejected: "error", paused: "error", disabled: "muted",
};
export function statusLabel(status: string): string {
  const k = "status." + status;
  return hasKey(k) ? tr(k) : status;
}
export function Pill({ status, children }: { status: string; children?: ReactNode }) {
  useI18n(); // re-render on language change
  return <span className={"pill pill-" + (PILL[status] ?? "muted")}>{children ?? statusLabel(status)}</span>;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
/** Open dialogs: only the top dialog reacts to Esc / Tab (and to the Android back button, which sends Esc). */
const dialogStack: Array<{ order: number }> = [];
let dialogSeq = 0;
/** Render-order rank: a dialog nested in another (rendered later) ranks above it even when both mount together. */
const topDialog = () => dialogStack.reduce<{ order: number } | undefined>((top, d) => (!top || d.order > top.order ? d : top), undefined);
export function openDialogCount() { return dialogStack.length; }

/** Focusable, visible elements inside a dialog, in tab order. */
export function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.closest("[inert], [aria-hidden='true']"));
}

/**
 * Accessible-dialog behaviour shared by every dialog: registers on the dialog stack, moves focus into the dialog
 * (unless a field already took it via autoFocus), traps Tab / Shift+Tab, routes Esc to `onEscape` (top dialog only)
 * and restores focus to the element that opened it when it closes.
 */
function useDialog(ref: RefObject<HTMLElement | null>, onEscape: () => void) {
  const esc = useRef(onEscape);
  esc.current = onEscape;
  const [order] = useState(() => ++dialogSeq);
  useEffect(() => {
    const id = { order };
    dialogStack.push(id);
    const opener = document.activeElement as HTMLElement | null;
    const root = ref.current;
    if (root && !root.contains(document.activeElement)) root.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (topDialog() !== id || !ref.current || e.isComposing) return;
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); esc.current(); return; }
      if (e.key !== "Tab") return;
      const els = focusablesIn(ref.current);
      if (!els.length) { e.preventDefault(); ref.current.focus(); return; }
      const first = els[0], last = els[els.length - 1], active = document.activeElement;
      if (e.shiftKey && (active === first || active === ref.current || !ref.current.contains(active))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (active === last || !ref.current.contains(active))) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = dialogStack.indexOf(id);
      if (i >= 0) dialogStack.splice(i, 1);
      if (opener && opener.isConnected && typeof opener.focus === "function") opener.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** Backdrop click that started AND ended on the backdrop (a text selection dragged out of a field doesn't close). */
function useBackdrop(onBackdrop: () => void) {
  const down = useRef<boolean | null>(null);
  return {
    onPointerDown: (e: ReactPointerEvent) => { down.current = e.target === e.currentTarget; },
    onClick: (e: ReactMouseEvent) => { if (e.target === e.currentTarget && down.current !== false) onBackdrop(); down.current = null; },
  };
}

export type DialogSize = "sm" | "md" | "lg" | "xl";

/**
 * Centered modal dialog for details and edit forms (app-wide; replaces the old side drawer).
 * Dimmed backdrop, sticky header (title + close) and footer (quiet Cancel + the actions passed in `footer`),
 * scrolling body; a full-screen sheet below 640px. Unsaved changes: Esc, ✕, Cancel and the Android back button ask
 * before discarding; a backdrop click only closes when nothing was changed. `dirty` overrides the automatic
 * tracking (any input/change event inside the body marks the form as changed; pass `dirty={false}` for search boxes).
 */
export function Dialog({ open, title, onClose, children, footer, size = "lg", dirty, cancel = true, className }: {
  open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; size?: DialogSize; dirty?: boolean; cancel?: boolean; className?: string;
}) {
  if (!open) return null;
  return <DialogInner title={title} onClose={onClose} footer={footer} size={size} dirty={dirty} cancel={cancel} className={className}>{children}</DialogInner>;
}

function DialogInner({ title, onClose, children, footer, size, dirty, cancel, className }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; size: DialogSize; dirty?: boolean; cancel: boolean; className?: string }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const [edited, setEdited] = useState(false);
  const [asking, setAsking] = useState(false);
  const isDirty = dirty ?? edited;
  const requestClose = () => { if (isDirty) setAsking(true); else onClose(); };
  useDialog(ref, requestClose);
  const backdrop = useBackdrop(() => { if (!isDirty) onClose(); });
  const titleId = useRef("dlg-" + Math.random().toString(36).slice(2, 9)).current;
  const markEdited = () => { if (!edited) setEdited(true); };
  return (
    <>
      <div className="overlay center dialog-overlay" {...backdrop}>
        <div ref={ref} tabIndex={-1} className={"dialog dialog-" + size + (className ? " " + className : "")} role="dialog" aria-modal="true" aria-labelledby={titleId} data-dirty={isDirty ? "true" : undefined}>
          <header className="dialog-head">
            <h2 id={titleId}>{title}</h2>
            <button type="button" className="icon-btn" aria-label={t("common.close")} onClick={requestClose}>✕</button>
          </header>
          <div className="dialog-body" onChange={markEdited} onInput={markEdited}>{children}</div>
          {footer && <footer className="dialog-foot" onClickCapture={(e) => { if ((e.target as HTMLElement).closest(".btn-primary")) setEdited(false); }}>
            {cancel && <button type="button" className="btn btn-quiet dialog-cancel" onClick={requestClose}>{t("common.cancel")}</button>}
            {footer}
          </footer>}
        </div>
      </div>
      <Confirm open={asking} title={t("ui.discard.title")} body={t("ui.discard.body")} confirmLabel={t("ui.discard.confirm")} cancelLabel={t("ui.discard.keep")} danger
        onConfirm={() => { setAsking(false); onClose(); }} onCancel={() => setAsking(false)} />
    </>
  );
}

/** Details / edit forms. Kept as the historical name; it is a centered modal `Dialog` now (`wide` = 960px for complex forms). */
export function Drawer({ open, title, onClose, children, footer, wide, dirty, cancel }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; dirty?: boolean; cancel?: boolean }) {
  return <Dialog open={open} title={title} onClose={onClose} footer={footer} size={wide ? "xl" : "lg"} dirty={dirty} cancel={cancel}>{children}</Dialog>;
}

/** Compact dialog (POS line editor, approvals, server setting): same chrome, 560px; the caller's footer has its own buttons. */
export function Modal({ open, title, onClose, children, footer, dirty }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; dirty?: boolean }) {
  return <Dialog open={open} title={title} onClose={onClose} footer={footer} size="md" dirty={dirty ?? false} cancel={false}>{children}</Dialog>;
}

/** Confirmation dialog for consequential actions (cancel, move, discard changes). */
export function Confirm({ open, title, body, confirmLabel, cancelLabel, danger, busy, onConfirm, onCancel }: { open: boolean; title: string; body: ReactNode; confirmLabel: string; cancelLabel?: string; danger?: boolean; busy?: boolean; onConfirm: () => void; onCancel: () => void }) {
  if (!open) return null;
  return <ConfirmInner title={title} body={body} confirmLabel={confirmLabel} cancelLabel={cancelLabel} danger={danger} busy={busy} onConfirm={onConfirm} onCancel={onCancel} />;
}

function ConfirmInner({ title, body, confirmLabel, cancelLabel, danger, busy, onConfirm, onCancel }: { title: string; body: ReactNode; confirmLabel: string; cancelLabel?: string; danger?: boolean; busy?: boolean; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  useDialog(ref, () => { if (!busy) onCancel(); });
  const backdrop = useBackdrop(() => { if (!busy) onCancel(); });
  return (
    <div className="overlay center dialog-overlay confirm-overlay" {...backdrop}>
      <div ref={ref} tabIndex={-1} className="modal dialog-confirm" role="alertdialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        <div className="muted">{body}</div>
        <div className="modal-actions">
          <button type="button" className="btn btn-quiet" onClick={onCancel} disabled={busy}>{cancelLabel ?? t("common.back")}</button>
          <button type="button" className={"btn " + (danger ? "btn-danger" : "btn-primary")} onClick={onConfirm} disabled={busy}>{busy ? t("common.working") : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function ErrorNote({ text }: { text: string | null }) {
  if (!text) return null;
  return <div className="note note-error" role="alert">{text}</div>;
}

export function PageHead({ title, sub, action }: { title: string; sub?: ReactNode; action?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <div className="muted">{sub}</div>}
      </div>
      {action && <div className="page-actions">{action}</div>}
    </div>
  );
}

/** Empty state with guidance and (optionally) the one next action. */
export function EmptyState({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-mark" aria-hidden>○</div>
      <h3>{title}</h3>
      {body && <p className="muted">{body}</p>}
      {action}
    </div>
  );
}

/** Loading placeholder lines/blocks. */
export function Skeleton({ lines = 3, block }: { lines?: number; block?: boolean }) {
  const { t } = useI18n();
  return (
    <div className="skeleton-wrap" aria-busy="true" aria-label={t("common.loading")}>
      {block ? <div className="skeleton skeleton-block" /> : Array.from({ length: lines }, (_, i) => <div key={i} className="skeleton" style={{ width: (90 - i * 15) + "%" }} />)}
    </div>
  );
}

// ---------------------------------------------------------------- toasts
type Toast = { id: number; text: string; kind: "ok" | "error" };
const ToastCtx = createContext<(text: string, kind?: Toast["kind"]) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: Toast["kind"] = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((ts) => [...ts.slice(-2), { id, text, kind }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), kind === "error" ? 6000 : 3500);
  }, []);
  useEffect(() => installUnhandledErrorToasts(push), [push]);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((x) => <div key={x.id} className={"toast toast-" + x.kind} role={x.kind === "error" ? "alert" : undefined}>{x.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

/** Labelled form field wrapper. */
export function Field({ label, hint, children, error }: { label: ReactNode; hint?: ReactNode; children: ReactNode; error?: string | null }) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {error ? <span className="field-error">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

/** Amount input in major units for a currency, emitting integer minor units. */
export function AmountInput({ value, onChange, currency, label, autoFocus, bare }: { value: number | null; onChange: (minor: number | null) => void; currency: string; label: string; autoFocus?: boolean; bare?: boolean }) {
  const exp = currencyExponent(currency);
  const show = (v: number | null) => (v === null ? "" : minorToDecimal(v, currency));
  const [text, setText] = useState(show(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setText(show(value)); /* external change */ }, [value, currency, focused]);
  const input = (
    <div className="amount">
      <span className="muted">{currency}</span>
      <input inputMode="decimal" autoFocus={autoFocus} value={text} aria-label={bare ? label : undefined} onFocus={() => setFocused(true)}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d.]/g, "");
          setText(v);
          const m = new RegExp("^(\\d+)(?:\\.(\\d{0," + exp + "}))?$").exec(v);
          onChange(m ? Number(m[1]) * 10 ** exp + Number((m[2] ?? "").padEnd(exp, "0") || 0) : null);
        }}
        onBlur={() => { setFocused(false); setText(show(value)); }} />
    </div>
  );
  if (bare) return input;
  return (
    <label className="field">
      <span className="label">{label}</span>
      {input}
    </label>
  );
}
